<script setup>
import { faBuildingColumns } from '@fortawesome/pro-regular-svg-icons'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import { isMobileDevice } from '~/libs/device-type'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useBankPullStatusUiStore } from '~/store/bank-pull-status-ui'

// Set on sidebar mount points (the portal aside, and the embedded aside, which overrides the bank
// icon color with the affiliate's nav text color), so the chip renders unpilled with the bank
// glyph leading, instead of the light-surface pill used everywhere else (marketplace top bar,
// embedded mobile header).
defineProps({
  dark: {
    type: Boolean,
    default: false,
  },
  // Hang the popover to the left of the chip, right edges aligned, for a chip at the right end of
  // a bar (the desktop marketplace top nav). Off by default: the sidebar and embedded chips sit at
  // the left of the screen, where a leftward panel would run off it.
  popoverAlignEnd: {
    type: Boolean,
    default: false,
  },
})

const store = useBankPullStatusUiStore()
const affiliateCustomizationStore = useAffiliateCustomizationStore()

// The popover panel is teleported onto <body>, out of reach of the affiliate vars (--app-* and the
// --tw-color-* token overrides) the layout sets on its own container, so in affiliate contexts (the
// same isEmbeddedLayout check layouts/portal.vue uses to pick appStyleProps) they are bound onto
// the panel directly.
const popoverThemeVars = computed(() => (
  affiliateCustomizationStore.isEmbeddedLayout ? affiliateCustomizationStore.appStyleProps : null
))

// Evaluated once - device type doesn't change mid-session. Pointer devices open the popover on
// hover (AC-8); touch devices toggle it on tap and dismiss it via v-click-outside (AC-9), since
// touch doesn't reliably fire hover events.
const isTouchDevice = typeof window !== 'undefined' ? isMobileDevice() : false

const chipRef = ref(null)
const popoverRef = ref(null)

// Latches true for the duration of ONE appearance - long enough to survive the store's own
// shared latch committing mid-animation (a deferred tick, see bank-pull-status-ui.js) and
// flipping showAttentionPulse back to false for every instance - but resets the instant this
// instance's v-if drops it, so a persistently-mounted chip (the real Slice 5 shape) that later
// becomes visible AGAIN in a later, independent appearance re-evaluates fresh instead of
// replaying the CSS animation on every v-if remount for the rest of this component's life.
const showAttention = ref(false)
watch(() => [store.visible, store.showAttentionPulse], ([isVisible, shouldPulse]) => {
  if (!isVisible) {
    showAttention.value = false
    return
  }
  if (!shouldPulse) return
  if (!showAttention.value) {
    showAttention.value = true
    return
  }
  // Already true - e.g. a borrower switch mid-appearance where store.visible never dropped, so
  // this component never reset. Setting the same value again is a no-op for the DOM class, and a
  // class that never actually leaves the element never replays its CSS animation. Force a real
  // off/on cycle across two ticks so the browser sees an actual attribute change.
  showAttention.value = false
  nextTick(() => { showAttention.value = true })
}, { immediate: true })

const motionQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null

const prefersReducedMotion = ref(motionQuery?.matches ?? false)

function handleMotionChange(event) {
  prefersReducedMotion.value = event.matches
}

// Tracked locally, not just fired-and-forgotten into the shared store, so onUnmounted below can
// defensively release the store's hover count if this instance is removed from the DOM (e.g. its
// v-if flips false) while the pointer is still over it - the browser never fires mouseleave on a
// node that's already gone, which would otherwise leave the shared count stuck above zero and the
// 20s completed-state hold timer permanently paused for every instance, forever.
const isHovering = ref(false)
function handleMouseEnter(event) {
  if (isHovering.value) return
  isHovering.value = true
  store.startHover()
  if (!isTouchDevice) popoverRef.value?.show(event)
}
// The popover panel is teleported onto document.body (see UiPopover's <Teleport>), so it is
// not a DOM descendant of the chip - moving the pointer from the chip toward the panel fires a
// real mouseleave on the chip before the cursor ever reaches the panel's content. Checking
// event.relatedTarget against both the chip's own subtree and the (teleported) popover content
// tells a "leaving toward the panel" mouseleave apart from a "leaving the widget entirely" one, so
// the popover doesn't close out from under the pointer mid-travel.
function isPointerWithinChipOrPopover(target) {
  return !!(chipRef.value?.contains(target) || popoverRef.value?.contains(target))
}

function handleMouseLeave(event) {
  if (!isHovering.value) return
  if (isPointerWithinChipOrPopover(event.relatedTarget)) return
  isHovering.value = false
  store.endHover()
  if (!isTouchDevice) popoverRef.value?.hide()
}

function handleClick(event) {
  if (!isTouchDevice) return
  popoverRef.value?.toggle(event)
}

// The click-outside directive below only checks containment against the trigger element (see
// plugins/045.click-outside.js), and the popover panel itself is teleported onto document.body -
// outside that element's DOM subtree - so a tap inside the panel would otherwise be
// misclassified as "outside" and close it immediately. UiPopover exposes the real containment
// check against its (possibly teleported) content so this doesn't have to hardcode a selector.
function handleClickOutside(event) {
  if (!isTouchDevice) return
  if (popoverRef.value?.contains(event.target)) return
  popoverRef.value?.hide()
}

function _releaseHoverIfNeeded() {
  if (isHovering.value) {
    isHovering.value = false
    store.endHover()
  }
}

onMounted(() => {
  motionQuery?.addEventListener('change', handleMotionChange)
})

onUnmounted(() => {
  _releaseHoverIfNeeded()
  motionQuery?.removeEventListener('change', handleMotionChange)
})

// onUnmounted above only fires when this whole component instance is destroyed. In the real
// Slice 5 shape this component stays persistently mounted and only the div below is toggled by
// v-if, so a v-if removal (e.g. after a manual upload) never unmounts the component and never
// fires mouseleave on the now-detached node - onUnmounted alone would leave hoverCount stuck
// above zero, permanently pausing the completed-state hold for every instance.
watch(() => store.visible, (visible) => {
  if (!visible) _releaseHoverIfNeeded()
})

const transitionName = computed(() => (prefersReducedMotion.value ? '' : 'bank-pull-chip'))
const showAttentionClass = computed(() => showAttention.value && !prefersReducedMotion.value)
</script>

<template>
  <Transition :name="transitionName" appear>
    <div
      v-if="store.visible"
      ref="chipRef"
      class="bank-pull-status-chip tw:flex tw:w-fit tw:shrink-0 tw:items-center tw:gap-2"
      :class="[
        showAttentionClass ? 'bank-pull-status-chip--attention' : null,
        dark
          ? null
          : 'tw:rounded-full tw:border! tw:border-w-neutral-100! tw:bg-white tw:px-3 tw:py-2 tw:shadow-sm',
      ]"
      data-cy="bank-pull-status-chip"
      v-click-outside="handleClickOutside"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
    >
      <!-- On the light (default) theme the status icon leads; on dark (portal sidebar, no pill
           background) the bank glyph leads instead, per design. -->
      <BankPullStatusIcon :state="store.indicatorState" :class="dark ? 'tw:order-2' : null" />
      <font-awesome-icon
        :icon="faBuildingColumns"
        class="bank-pull-status-chip__bank-icon"
        :class="dark ? 'tw:order-1 tw:text-w-neutral-100' : 'tw:text-w-neutral-700'"
        data-cy="bank-pull-status-chip-bank-icon"
      />
      <UiPopover
        ref="popoverRef"
        class="bank-pull-status-popover-panel"
        :style="popoverThemeVars"
        :align-end="popoverAlignEnd"
      >
        <div @mouseleave="handleMouseLeave">
          <BankPullStatusPopover />
        </div>
      </UiPopover>
    </div>
  </Transition>
</template>

<style scoped>
.bank-pull-chip-enter-from,
.bank-pull-chip-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.9);
}

.bank-pull-chip-enter-active,
.bank-pull-chip-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.bank-pull-status-chip--attention {
  /* Delayed to start after the 200ms entry transition finishes - a CSS animation always wins
     over a transition targeting the same property (transform) while both are active, so
     starting immediately would override the entry's translateY/scale-in for its first 200ms. */
  animation: bank-pull-status-chip-attention-pulse 600ms ease-in-out 2;
  animation-delay: 200ms;
}

@keyframes bank-pull-status-chip-attention-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}
</style>

<style>
/* Centred on the screen below 992px, whatever the chip's position (the popup part is what
   wa-popup positions with inline left/top, hence !important). */
@media (max-width: 991px) {
  wa-popup.bank-pull-status-popover-panel::part(popup) {
    left: 50% !important;
    transform: translateX(-50%) !important;
    min-width: 0 !important;
    max-width: calc(100vw - 32px) !important;
  }
}
</style>
